import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { createPageUrl } from '../utils';
// (base44 mock client removed — events + custom characters use /api/* now)
import { useAuth } from '../firebase/AuthContext';
import { useLanguage } from '../components/LanguageContext';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectItem, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Calendar, Plus, ArrowLeft, Image as ImageIcon, Search, Type, Minus, Frame, Edit, Trash2, Play } from 'lucide-react';
import { CHARACTERS, CATEGORIES, getCharacterById } from '../components/data/characters';
import { toast } from 'sonner';
import FramePreview from '../components/photobooth/FramePreview';
import InteractiveFrameEditor from '../components/photobooth/InteractiveFrameEditor';
import { POLAROID_COLORS } from '../utils/polaroidLayout';

const EVENT_TYPES = [
  { id: 'wedding', he: 'חתונה', en: 'Wedding' },
  { id: 'birthday', he: 'יום הולדת', en: 'Birthday' },
  { id: 'corporate', he: 'אירוע עסקי', en: 'Corporate' },
  { id: 'party', he: 'מסיבה', en: 'Party' },
];

const TEMPLATE_CARDS = [
  { id: 'classic', he: 'קלסי אלגנטי', en: 'Classic Elegant', thumb: 'from-amber-700 via-amber-500 to-yellow-300', emoji: '✨' },
  { id: 'neon', he: 'ניאון מסיבה', en: 'Neon Party', thumb: 'from-fuchsia-700 via-cyan-400 to-blue-300', emoji: '⚡' },
  { id: 'kids', he: 'ילדים צבעוני', en: 'Colorful Kids', thumb: 'from-pink-400 via-yellow-300 to-green-400', emoji: '🎉' },
  { id: 'business', he: 'עסקי יוקרתי', en: 'Premium Business', thumb: 'from-slate-700 via-purple-800 to-slate-900', emoji: '💼' },
];

// Presets applied when the user clicks a template
const TEMPLATE_PRESETS = {
  classic: {
    frameId: 'polaroid_portrait',
    frameColor: '#FFF8E7',
    elements: [{ id: 1, type: 'text', content: 'שם האירוע', x: 90, y: 450, fontSize: 28, color: '#1a1a1a', fontFamily: 'Playfair Display' }],
  },
  neon: {
    frameId: 'polaroid_portrait',
    frameColor: '#0F0F0F',
    elements: [{ id: 1, type: 'text', content: 'שם האירוע', x: 80, y: 450, fontSize: 32, color: '#22d3ee', fontFamily: 'Bebas Neue' }],
  },
  kids: {
    frameId: 'polaroid_portrait',
    frameColor: '#FFE4E6',
    elements: [
      { id: 1, type: 'text', content: 'יום הולדת שמח!', x: 70, y: 435, fontSize: 28, color: '#9d174d', fontFamily: 'Pacifico' },
      { id: 2, type: 'text', content: '🎈 🎂 🎈', x: 120, y: 485, fontSize: 22, color: '#f59e0b', fontFamily: 'Arial' },
    ],
  },
  business: {
    frameId: 'polaroid_landscape',
    frameColor: '#FFFFFF',
    elements: [{ id: 1, type: 'text', content: 'COMPANY EVENT', x: 130, y: 320, fontSize: 22, color: '#1e293b', fontFamily: 'Oswald' }],
  },
};

// Only Polaroid frames. The frame card color is configurable per event.
const FRAME_STYLES = [
  {
    id: 'polaroid_portrait',
    name: 'Polaroid Portrait',
    nameHe: 'פולארויד לאורך',
    orientation: 'portrait',
  },
  {
    id: 'polaroid_landscape',
    name: 'Polaroid Landscape',
    nameHe: 'פולארויד לרוחב',
    orientation: 'landscape',
  },
];

const initialFormState = {
  name: '',
  type: '',
  description: '',
  date: '',
  templateId: '',
  characterIds: [],
  customCharacterIds: [],
  frameId: 'polaroid_portrait',
  frameColor: '#FFFFFF',
  overlayText: '',
  watermarkText: '',
  logoUrl: '',
  logoSize: 100, // percentage
  textSize: 100, // percentage
  footerTitle: '',
  footerSubtitle: '',
  sideText: '',
  showDate: true,
  frameElements: [], // stores drag & drop text/logo elements from InteractiveFrameEditor
};

export default function Events() {
  const { language } = useLanguage();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState('list'); // 'list' | 'create' | 'edit'
  const [editingEventId, setEditingEventId] = useState(null);
  const [form, setForm] = useState(initialFormState);
  const [characterSearch, setCharacterSearch] = useState('');
  const [characterCategory, setCharacterCategory] = useState('all');
  const [selectedFrame, setSelectedFrame] = useState(null);

  const { user, getAuthToken } = useAuth();

  // ── Events — backed by SQLite via /api/events ────────────────────────
  const {
    data: eventsData = [],
    isLoading,
    isFetching,
    refetch: refetchEvents,
  } = useQuery({
    queryKey: ['events', user?.uid],
    queryFn: async () => {
      const token = await getAuthToken();
      if (!token) return [];
      const r = await fetch('/api/events', { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      // Server returns a plain array; fall back to {events:[...]} if old proxy
      const j = await r.json();
      return Array.isArray(j) ? j : (j.events || []);
    },
    enabled: !!user,
  });
  const events = eventsData;

  // ── Custom characters — backed by SQLite via /api/custom-characters ─
  const {
    data: customCharsData = [],
    isLoading: isLoadingCustomCharacters,
  } = useQuery({
    queryKey: ['customCharacters', user?.uid],
    queryFn: async () => {
      const token = await getAuthToken();
      if (!token) return [];
      const r = await fetch('/api/custom-characters', { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      return Array.isArray(j) ? j : (j.characters || []);
    },
    enabled: !!user,
  });
  const customCharacters = customCharsData;

  const eventToForm = (event) => ({
    name: event.event_name || '',
    type: event.event_type || '',
    description: event.description || '',
    date: event.event_date || '',
    templateId: event.template_id || '',
    characterIds: Array.isArray(event.allowed_characters) ? event.allowed_characters : [],
    customCharacterIds: Array.isArray(event.custom_character_ids) ? event.custom_character_ids : [],
    frameId: event.frame_id || 'polaroid_portrait',
    frameColor: event.frame_color || '#FFFFFF',
    overlayText: event.overlay_text || '',
    watermarkText: event.watermark_text || '',
    logoUrl: event.logo_url || '',
    logoSize: 100,
    textSize: 100,
    footerTitle: '',
    footerSubtitle: '',
    sideText: '',
    showDate: true,
    frameElements: Array.isArray(event.frame_elements) ? event.frame_elements : [],
  });

  const buildEventPayload = () => ({
    event_name: form.name,
    event_type: form.type,
    description: form.description,
    event_date: form.date,
    template_id: form.templateId,
    allowed_characters: form.characterIds,
    custom_character_ids: form.customCharacterIds,
    frame_id: form.frameId,
    frame_color: form.frameColor,
    frame_elements: form.frameElements,
    overlay_text: form.overlayText,
    watermark_text: form.watermarkText,
    logo_url: form.logoUrl,
  });

  const openEditEvent = (event) => {
    setEditingEventId(event.id);
    setForm(eventToForm(event));
    setMode('edit');
  };

  const openCreateEvent = () => {
    setEditingEventId(null);
    setForm(initialFormState);
    setCharacterSearch('');
    setCharacterCategory('all');
    setMode('create');
  };

  const cancelEdit = () => {
    setEditingEventId(null);
    setForm(initialFormState);
    setCharacterSearch('');
    setCharacterCategory('all');
    setMode('list');
  };

   const createEventMutation = useMutation({
     mutationFn: async (payload) => {
       const token = await getAuthToken();
       if (!token) throw new Error('Not authenticated — please log in');
       const r = await fetch('/api/events', {
         method: 'POST',
         headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
         body: JSON.stringify(payload),
       });
       const data = await r.json().catch(() => ({}));
       if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
       return data;
     },
     onSuccess: (data) => {
       const newEventId = data?.event?.id || data?.id;
       toast.success(
         language === 'he' ? 'האירוע נוצר בהצלחה!' : 'Event created successfully!',
         {
           action: {
             label: language === 'he' ? 'פתח בעמדה' : 'Open Booth',
             onClick: () => {
               // Store the event ID and navigate to Photobooth
               localStorage.setItem('base44_active_event_id', String(newEventId));
               window.location.href = createPageUrl('Photobooth');
             },
           },
           duration: 8000,
         }
       );
       setForm(initialFormState);
       setEditingEventId(null);
       setMode('list');
       refetchEvents();
     },
     onError: (err) => {
       toast.error(
         language === 'he' ? `יצירת האירוע נכשלה: ${err.message}` : `Failed to create event: ${err.message}`
       );
     },
   });

  const updateEventMutation = useMutation({
    mutationFn: async ({ id, payload }) => {
      const token = await getAuthToken();
      if (!token) throw new Error('Not authenticated — please log in');
      const r = await fetch(`/api/events/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
      return data;
    },
    onSuccess: () => {
      toast.success(
        language === 'he' ? 'האירוע עודכן בהצלחה' : 'Event updated successfully'
      );
      setForm(initialFormState);
      setEditingEventId(null);
      setMode('list');
      refetchEvents();
    },
    onError: (err) => {
      toast.error(
        language === 'he' ? `עדכון האירוע נכשל: ${err.message}` : `Failed to update event: ${err.message}`
      );
    },
  });

  const deleteEventMutation = useMutation({
    mutationFn: async (id) => {
      const token = await getAuthToken();
      if (!token) throw new Error('Not authenticated — please log in');
      const r = await fetch(`/api/events/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data?.error || `HTTP ${r.status}`);
      }
      return id;
    },
    onSuccess: () => {
      toast.success(
        language === 'he' ? 'האירוע נמחק בהצלחה' : 'Event deleted successfully'
      );
      setEditingEventId(null);
      setMode('list');
      refetchEvents();
    },
    onError: (err) => {
      toast.error(
        language === 'he' ? `מחיקת האירוע נכשלה: ${err.message}` : `Failed to delete event: ${err.message}`
      );
    },
  });

  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) =>
      (b.created_at || '').localeCompare(a.created_at || '')
    );
  }, [events]);

  const filteredCharacters = useMemo(() => {
    let list = CHARACTERS;
    if (characterCategory !== 'all') {
      list = list.filter((c) => c.category === characterCategory);
    }
    if (characterSearch.trim()) {
      const term = characterSearch.toLowerCase();
      list = list.filter((c) => {
        const he = c.name_he || '';
        const en = c.name_en || '';
        return (
          en.toLowerCase().includes(term) ||
          he.toLowerCase().includes(term)
        );
      });
    }
    return list;
  }, [characterCategory, characterSearch]);

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Apply a visual template: sets frameId + starter elements for the drag-drop editor
  const applyTemplate = (tplId) => {
    const preset = TEMPLATE_PRESETS[tplId];
    if (!preset) return;
    setForm((prev) => ({
      ...prev,
      templateId: tplId,
      frameId: preset.frameId,
      frameElements: preset.elements.map((el) => ({ ...el, id: Date.now() + el.id })),
    }));
    toast.success(
      language === 'he' ? `תבנית "${TEMPLATE_CARDS.find(t => t.id === tplId)?.he}" הוחלה!` : `Template applied!`,
      { duration: 2000 }
    );
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setForm(prev => ({ ...prev, logoUrl: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const toggleCharacter = (id) => {
    setForm((prev) => {
      const exists = prev.characterIds.includes(id);
      const nextIds = exists
        ? prev.characterIds.filter((cid) => cid !== id)
        : [...prev.characterIds, id];
      return { ...prev, characterIds: nextIds };
    });
  };

  const toggleCustomCharacter = (id) => {
    setForm((prev) => {
      const exists = prev.customCharacterIds.includes(id);
      const nextIds = exists
        ? prev.customCharacterIds.filter((cid) => cid !== id)
        : [...prev.customCharacterIds, id];
      return { ...prev, customCharacterIds: nextIds };
    });
  };

  const handleFrameSelect = (frame) => {
    setSelectedFrame(frame);
    handleFormChange('frameId', frame.id || 'polaroid_portrait');
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Only the name is genuinely required — that is all the backend enforces.
    // This used to also demand an event type and then report BOTH fields in one
    // message, so forgetting the type read as "the name is missing" to someone
    // who had just typed a name, with no way to tell which field was at fault.
    if (!String(form.name || '').trim()) {
      toast.error(
        language === 'he' ? 'צריך למלא שם לאירוע' : 'Please enter an event name'
      );
      const el = document.getElementById('event-name-input');
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); }
      return;
    }

    if (mode === 'edit' && editingEventId) {
      updateEventMutation.mutate({ id: editingEventId, payload: buildEventPayload() });
    } else {
      createEventMutation.mutate(buildEventPayload());
    }
  };

  const handleDeleteEvent = (event) => {
    const ok = window.confirm(
      language === 'he'
        ? `למחוק את האירוע "${event.event_name}"? פעולה זו לא ניתנת לביטול.`
        : `Delete event "${event.event_name}"? This cannot be undone.`
    );
    if (ok) deleteEventMutation.mutate(event.id);
  };

  const pageTitle = mode === 'list'
    ? language === 'he' ? 'האירועים שלי' : 'My Events'
    : mode === 'edit'
      ? (language === 'he' ? 'עריכת אירוע' : 'Edit Event')
      : (language === 'he' ? 'אירוע חדש' : 'New Event');

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white mb-1">{pageTitle}</h1>
            <p className="text-slate-400">
              {mode === 'list'
                ? language === 'he'
                  ? 'צור אירועים מותאמים אישית עם דמויות, מסגרות וקרדיטים'
                  : 'Create custom events with characters, frames and credits'
                : language === 'he'
                  ? 'הגדר את פרטי האירוע והתמונות המתאימות'
                  : 'Configure the event details and matching visuals'}
            </p>
          </div>

          <div className="flex items-center gap-3">
            {(mode === 'create' || mode === 'edit') && (
              <Button
                variant="ghost"
                className="text-slate-300 hover:text-white hover:bg-slate-800 flex items-center gap-2"
                onClick={cancelEdit}
              >
                <ArrowLeft className="w-4 h-4" />
                {language === 'he' ? 'חזרה לאירועים' : 'Back to events'}
              </Button>
            )}
            {mode === 'list' && (
              <Button
                className="bg-violet-600 hover:bg-violet-700 text-white flex items-center gap-2"
                onClick={() => setMode('create')}
              >
                <Plus className="w-4 h-4" />
                {language === 'he' ? 'אירוע חדש' : 'New Event'}
              </Button>
            )}
          </div>
        </div>

        {mode === 'list' ? (
          <Card className="glass-card border-slate-800">
            <CardContent className="p-8">
              {isLoading || isFetching ? (
                <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                  <Calendar className="w-14 h-14 mb-4 text-slate-500" />
                  <p>{language === 'he' ? 'טוען אירועים...' : 'Loading events...'}</p>
                </div>
              ) : sortedEvents.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Calendar className="w-16 h-16 mb-4 text-slate-500" />
                  <h2 className="text-xl font-semibold text-white mb-2">
                    {language === 'he'
                      ? 'אין אירועים עדיין'
                      : 'No events yet'}
                  </h2>
                  <p className="text-slate-400 mb-6 max-w-md">
                    {language === 'he'
                      ? 'צור את האירוע הראשון שלך עם דמויות מותאמות אישית וחוויית צילום מיוחדת.'
                      : 'Create your first event with custom characters and a unique photo experience.'}
                  </p>
                  <Button
                    className="bg-violet-600 hover:bg-violet-700 text-white flex items-center gap-2"
                    onClick={() => setMode('create')}
                  >
                    <Plus className="w-4 h-4" />
                    {language === 'he' ? 'צור אירוע חדש' : 'Create new event'}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {sortedEvents.map((event) => (
                    <div
                      key={event.id}
                      className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 rounded-xl border border-slate-800 bg-slate-900/40 px-5 py-4"
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600/40 to-pink-500/40 flex items-center justify-center">
                          <ImageIcon className="w-6 h-6 text-violet-200" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-white mb-1">
                            {event.event_name}
                          </h3>
                          <p className="text-sm text-slate-400 mb-1">
                            {event.description ||
                              (language === 'he'
                                ? 'אירוע שנוצר במערכת'
                                : 'Event created in the system')}
                          </p>
                          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
                            {event.event_type && (
                              <span className="inline-flex items-center rounded-full border border-slate-700 px-2 py-0.5">
                                {event.event_type}
                              </span>
                            )}
                            {event.event_date && (
                              <span className="inline-flex items-center gap-1 rounded-full border border-slate-700 px-2 py-0.5">
                                <Calendar className="w-3 h-3" />
                                {event.event_date}
                              </span>
                            )}
                            {Array.isArray(event.custom_character_ids) && event.custom_character_ids.length > 0 && (
                              <span className="inline-flex items-center rounded-full border border-slate-700 px-2 py-0.5">
                                {language === 'he'
                                  ? `דמויות מותאמות (${event.custom_character_ids.length})`
                                  : `Custom (${event.custom_character_ids.length})`}
                              </span>
                            )}
                            {Array.isArray(event.allowed_characters) && event.allowed_characters.length > 0 && (
                              <span className="inline-flex items-center rounded-full border border-slate-700 px-2 py-0.5">
                                {language === 'he' ? 'דמויות: ' : 'Characters: '}
                                {(() => {
                                  const names = event.allowed_characters
                                    .map((id) => getCharacterById(id))
                                    .filter(Boolean)
                                    .map((c) => (language === 'he' ? c.name_he : c.name_en));
                                  const shown = names.slice(0, 3).join(', ');
                                  const extra = names.length > 3 ? ` +${names.length - 3}` : '';
                                  return `${shown}${extra}`;
                                })()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 shrink-0">
                        <Button 
                          className="w-full sm:w-auto bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white flex items-center gap-2"
                          onClick={() => {
                            localStorage.setItem('base44_active_event_id', String(event.id));
                            window.location.href = createPageUrl('Photobooth');
                          }}
                        >
                          <Play className="w-4 h-4" />
                          {language === 'he' ? 'פתח בעמדה' : 'Open Booth'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full sm:w-auto border-violet-500/40 text-violet-200 hover:bg-violet-500/20 flex items-center gap-2"
                          onClick={() => openEditEvent(event)}
                        >
                          <Edit className="w-4 h-4" />
                          {language === 'he' ? 'ערוך והוסף דמויות' : 'Edit & Add Characters'}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          className="w-full sm:w-auto text-red-400 hover:bg-red-500/10 hover:text-red-300 flex items-center gap-2"
                          onClick={() => handleDeleteEvent(event)}
                        >
                          <Trash2 className="w-4 h-4" />
                          {language === 'he' ? 'מחק' : 'Delete'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <Card className="glass-card border-slate-800">
              <CardHeader>
                <CardTitle className="text-white text-xl">
                  {language === 'he' ? 'פרטי אירוע' : 'Event Details'}
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-6 md:grid-cols-2">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-slate-200">
                      {language === 'he' ? 'שם האירוע' : 'Event name'}
                    </Label>
                    <Input
                      id="event-name-input"
                      className="bg-slate-900/60 border-slate-800 text-slate-100"
                      placeholder={
                        language === 'he'
                          ? 'לדוגמה: חתונת יעל ויואב'
                          : 'e.g. Yael & Yoav Wedding'
                      }
                      value={form.name}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-200">
                      {language === 'he' ? 'סוג האירוע' : 'Event type'}
                    </Label>
                    <Select
                      value={form.type}
                      onValueChange={(value) => handleFormChange('type', value)}
                      className="bg-slate-900/60 border-slate-800 text-slate-100"
                    >
                      <SelectValue
                        placeholder={
                          language === 'he' ? 'בחר סוג אירוע' : 'Select event type'
                        }
                      />
                      {EVENT_TYPES.map((type) => (
                        <SelectItem key={type.id} value={type[language === 'he' ? 'he' : 'en']}>
                          {type[language === 'he' ? 'he' : 'en']}
                        </SelectItem>
                      ))}
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-200">
                      {language === 'he' ? 'תאריך האירוע' : 'Event date'}
                    </Label>
                    <Input
                      type="text"
                      className="bg-slate-900/60 border-slate-800 text-slate-100"
                      placeholder={language === 'he' ? 'dd/mm/yyyy' : 'dd/mm/yyyy'}
                      value={form.date}
                      onChange={(e) => handleFormChange('date', e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-200">
                    {language === 'he' ? 'תיאור האירוע' : 'Event description'}
                  </Label>
                  <Textarea
                    className="bg-slate-900/60 border-slate-800 text-slate-100 min-h-[160px]"
                    placeholder={
                      language === 'he'
                        ? 'תאר בקצרה את האירוע, המיקום וסוג האורחים.'
                        : 'Briefly describe the event, location and type of guests.'
                    }
                    value={form.description}
                    onChange={(e) => handleFormChange('description', e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card border-slate-800">
              <CardHeader>
                <CardTitle className="text-white text-xl">
                  {language === 'he' ? 'בחר דמויות לאירוע' : 'Select characters for this event'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-400">
                  {language === 'he'
                    ? 'בחר אילו דמויות יהיו זמינות בעמדה באירוע זה. כך האורחים יראו רק את מה שמתאים לאירוע.'
                    : 'Choose which characters will be available at the booth for this event only.'}
                </p>

                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                    {CATEGORIES.map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => setCharacterCategory(cat.id)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${characterCategory === cat.id
                          ? 'bg-violet-600 text-white'
                          : 'bg-slate-900/70 text-slate-400 hover:text-white hover:bg-slate-800'
                          }`}
                      >
                        {language === 'he' ? cat.name_he : cat.name_en}
                      </button>
                    ))}
                  </div>

                  <div className="relative w-full md:max-w-xs mt-2 md:mt-0">
                    <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input
                      className="pl-9 bg-slate-900/60 border-slate-800 text-slate-100 placeholder:text-slate-500 h-9"
                      placeholder={
                        language === 'he' ? 'חפש דמות...' : 'Search characters...'
                      }
                      value={characterSearch}
                      onChange={(e) => setCharacterSearch(e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 max-h-[260px] overflow-y-auto custom-scrollbar pe-1">
                  {filteredCharacters.map((char) => {
                    const selected = form.characterIds.includes(char.id);
                    return (
                      <button
                        key={char.id}
                        type="button"
                        onClick={() => toggleCharacter(char.id)}
                        className={`relative group rounded-xl overflow-hidden aspect-[3/4] border transition-all ${selected
                          ? 'border-violet-500 ring-2 ring-violet-500 ring-offset-2 ring-offset-slate-950'
                          : 'border-slate-800 hover:border-violet-500/60'
                          }`}
                      >
                        <img
                          src={char.image_url}
                          alt={language === 'he' ? char.name_he : char.name_en}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-2">
                          <span className="text-xs font-medium text-white">
                            {language === 'he' ? char.name_he : char.name_en}
                          </span>
                        </div>
                        {selected && (
                          <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-violet-600 flex items-center justify-center text-xs font-bold text-white">
                            ✓
                          </div>
                        )}
                      </button>
                    );
                  })}
                  {filteredCharacters.length === 0 && (
                    <div className="col-span-full text-center py-8 text-slate-500 text-sm">
                      {language === 'he' ? 'לא נמצאו דמויות' : 'No characters found'}
                    </div>
                  )}
                </div>

                <p className="text-xs text-slate-500">
                  {language === 'he'
                    ? `נבחרו ${form.characterIds.length} דמויות לאירוע זה.`
                    : `${form.characterIds.length} characters selected for this event.`}
                </p>
              </CardContent>
            </Card>

            <Card className="glass-card border-slate-800">
              <CardHeader>
                <CardTitle className="text-white text-xl">
                  {language === 'he'
                    ? 'דמויות מותאמות אישית לאירוע'
                    : 'Custom characters for this event'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {isLoadingCustomCharacters ? (
                  <p className="text-sm text-slate-400">
                    {language === 'he'
                      ? 'טוען דמויות מותאמות אישית...'
                      : 'Loading custom characters...'}
                  </p>
                ) : customCharacters.length === 0 ? (
                  <div className="text-sm text-slate-400 space-y-2">
                    <p>
                      {language === 'he'
                        ? 'עדיין לא יצרת דמות מותאמת אישית.'
                        : 'You have not created any custom characters yet.'}
                    </p>
                    <Link
                      to={createPageUrl('CustomCharacter')}
                      className="inline-flex items-center gap-2 text-violet-400 hover:text-violet-300 text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      {language === 'he' ? 'צור דמות מותאמת אישית' : 'Create a custom character'}
                    </Link>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-slate-400">
                      {language === 'he'
                        ? 'בחר אילו דמויות מותאמות יהיו זמינות באירוע זה.'
                        : 'Pick which custom characters will be available for this event.'}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 max-h-72 overflow-y-auto custom-scrollbar pe-1">
                      {customCharacters.map((char) => {
                        const selected = form.customCharacterIds.includes(char.id);
                        const thumb =
                          char.background_image?.data_url ||
                          (Array.isArray(char.style_images) && char.style_images[0]?.data_url) ||
                          '';
                        return (
                          <button
                            key={char.id}
                            type="button"
                            onClick={() => toggleCustomCharacter(char.id)}
                            className={`relative rounded-xl border text-left p-3 flex gap-3 bg-slate-900/60 transition-all ${selected
                              ? 'border-emerald-500 shadow-[0_0_25px_rgba(16,185,129,0.4)]'
                              : 'border-slate-800 hover:border-violet-500/60'
                              }`}
                          >
                            <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-800 flex-shrink-0 flex items-center justify-center">
                              {thumb ? (
                                <img
                                  src={thumb}
                                  alt={char.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <ImageIcon className="w-6 h-6 text-slate-500" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <span className="text-sm font-semibold text-white truncate" title={char.name}>
                                  {char.name}
                                </span>
                                {selected && (
                                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                                    {language === 'he' ? 'נבחר' : 'Selected'}
                                  </span>
                                )}
                              </div>
                              {char.prompt && (
                                <p className="text-[11px] text-slate-400 line-clamp-2">
                                  {char.prompt}
                                </p>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-slate-500">
                      {language === 'he'
                        ? `נבחרו ${form.customCharacterIds.length} דמויות מותאמות לאירוע זה.`
                        : `${form.customCharacterIds.length} custom characters selected for this event.`}
                    </p>
                  </>
                )}
              </CardContent>
            </Card>

            <Card className="glass-card border-slate-800">
              <CardHeader>
                <CardTitle className="text-white text-xl">
                  {language === 'he' ? 'בחר תבנית חזותית' : 'Choose visual template'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-400 mb-4">
                  {language === 'he'
                    ? 'בחר סגנון שמתאים לאווירת האירוע – ניתן לשנות מאוחר יותר.'
                    : 'Pick a style that matches the event mood – you can change it later.'}
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {TEMPLATE_CARDS.map((tpl) => {
                    const selected = form.templateId === tpl.id;
                    return (
                      <button
                        key={tpl.id}
                        type="button"
                        onClick={() => applyTemplate(tpl.id)}
                        className={`group relative rounded-xl border px-3 py-4 text-left transition-all ${selected
                          ? 'border-violet-500 bg-violet-500/20 shadow-[0_0_25px_rgba(139,92,246,0.4)]'
                          : 'border-slate-800 bg-slate-900/40 hover:border-violet-500/60 hover:bg-slate-900'
                          }`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-semibold text-white">
                            {tpl[language === 'he' ? 'he' : 'en']}
                          </span>
                          <span className="text-lg">{tpl.emoji}</span>
                        </div>
                        {/* Template-specific thumbnail */}
                        <div className={`h-16 rounded-lg bg-gradient-to-br ${tpl.thumb} opacity-70 group-hover:opacity-100 transition-opacity`} />
                        {selected && (
                          <div className="absolute top-2 left-2 text-xs bg-violet-500 text-white rounded px-1.5 py-0.5">
                            {language === 'he' ? 'פעיל' : 'Active'}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Frame Selection Card - with live FramePreview */}
            <Card className="glass-card border-slate-800">
              <CardHeader>
                <CardTitle className="text-white text-xl flex items-center gap-2">
                  <Frame className="w-5 h-5 text-violet-400" />
                  {language === 'he' ? 'בחירת מסגרת' : 'Frame Selection'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-5">
                  {/* Orientation picker — portrait / landscape Polaroid */}
                  <div>
                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">
                      {language === 'he' ? 'אוריינטציה' : 'Orientation'}
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      {FRAME_STYLES.map((style) => (
                        <button
                          key={style.id}
                          type="button"
                          onClick={() => handleFormChange('frameId', style.id)}
                          className={`relative rounded-xl p-4 transition-all flex flex-col items-center gap-2 ${form.frameId === style.id
                            ? 'bg-violet-600/20 ring-2 ring-violet-500'
                            : 'bg-slate-800/60 ring-1 ring-slate-700 hover:ring-slate-500'
                            }`}
                        >
                          <FramePreview
                            frameId={style.id}
                            frameColor={form.frameColor}
                            width={style.orientation === 'landscape' ? 140 : 90}
                          />
                          <p className="text-sm text-white font-medium">
                            {language === 'he' ? style.nameHe : style.name}
                          </p>
                          {form.frameId === style.id && (
                            <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-violet-600 flex items-center justify-center">
                              <span className="text-white text-xs">✓</span>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Color picker — preset swatches + custom color */}
                  <div>
                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">
                      {language === 'he' ? 'צבע המסגרת' : 'Frame Color'}
                    </label>
                    <div className="flex items-center gap-3 flex-wrap">
                      {POLAROID_COLORS.map((c) => (
                        <button
                          key={c.value}
                          type="button"
                          onClick={() => handleFormChange('frameColor', c.value)}
                          title={language === 'he' ? c.labelHe : c.label}
                          className={`w-10 h-10 rounded-full border-2 transition-transform hover:scale-110 ${
                            form.frameColor === c.value
                              ? 'border-violet-500 ring-2 ring-violet-500/40 scale-110'
                              : 'border-slate-600'
                          }`}
                          style={{ background: c.value }}
                        />
                      ))}
                      <div className="flex items-center gap-2 ml-2">
                        <input
                          type="color"
                          value={form.frameColor || '#FFFFFF'}
                          onChange={(e) => handleFormChange('frameColor', e.target.value)}
                          className="w-10 h-10 rounded-full cursor-pointer border-2 border-slate-600 bg-transparent"
                          title={language === 'he' ? 'צבע מותאם אישית' : 'Custom color'}
                        />
                        <span className="text-xs text-slate-400">
                          {language === 'he' ? 'מותאם' : 'Custom'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Live Preview */}
                  <div className="bg-slate-900/60 rounded-xl p-6 border border-slate-800">
                    <p className="text-xs text-slate-400 mb-4 text-center">
                      {language === 'he' ? 'תצוגה מקדימה חיה — מתעדכנת בזמן אמת' : 'Live Preview — updates in real time'}
                    </p>
                    <div className="flex justify-center">
                      <FramePreview
                        frameId={form.frameId}
                        frameColor={form.frameColor}
                        overlayText={form.overlayText}
                        logoUrl={form.logoUrl}
                        logoSize={form.logoSize}
                        textSize={form.textSize}
                        width={form.frameId === 'polaroid_landscape' ? 320 : 220}
                      />
                    </div>
                  </div>

                  {/* Hint */}
                  <p className="text-xs text-slate-500">
                    {language === 'he'
                      ? 'בחר אוריינטציה וצבע, ואז הוסף כיתוב/לוגו בעורך למטה.'
                      : 'Pick orientation and color, then add text/logo in the editor below.'}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Interactive Frame Editor - Drag & Drop */}
            <Card className="glass-card border-slate-800">
              <CardHeader>
                <CardTitle className="text-white text-xl flex items-center gap-2">
                  <ImageIcon className="w-5 h-5 text-violet-400" />
                  {language === 'he' ? 'עורך מסגרת אינטראקטיבי' : 'Interactive Frame Editor'}
                </CardTitle>
                <p className="text-sm text-slate-400 mt-1">
                  {language === 'he'
                    ? 'הוסף כיתובים ולוגואים, גרור למיקום הרצוי, ומשוך את הפינה לשינוי גודל'
                    : 'Add text and logos, drag to position, pull corner to resize'}
                </p>
              </CardHeader>
              <CardContent>
                <InteractiveFrameEditor
                  frameStyle={FRAME_STYLES.find(s => s.id === form.frameId) || FRAME_STYLES[0]}
                  frameColor={form.frameColor}
                  previewImage="/demos/girls/actress.jpg"
                  onChange={({ elements }) => handleFormChange('frameElements', elements)}
                  initialElements={form.frameElements || []}
                />
              </CardContent>
            </Card>

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="ghost"
                className="text-slate-300 hover:text-white hover:bg-slate-800"
                onClick={() => {
                  setForm(initialFormState);
                  setMode('list');
                }}
              >
                {language === 'he' ? 'ביטול' : 'Cancel'}
              </Button>
              <Button
                type="submit"
                className="bg-violet-600 hover:bg-violet-700 text-white"
                disabled={createEventMutation.isLoading || updateEventMutation.isLoading}
              >
                {createEventMutation.isLoading || updateEventMutation.isLoading
                  ? (language === 'he' ? 'שומר...' : 'Saving...')
                  : (mode === 'edit'
                    ? (language === 'he' ? 'שמור שינויים' : 'Save Changes')
                    : (language === 'he' ? 'שמור אירוע' : 'Save event'))}
              </Button>
            </div>
          </form>
        )}
      </div>

    </div>
  );
}
